import * as React from 'react';
import { styled } from '@linaria/react';

import VisuallyHidden from '@/components/VisuallyHidden';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  id: string;
  keybinding: string;
  isPressed: boolean;
  play: ({ id }: { id: string }) => void;
}

function DrumButton({
  id,
  keybinding,
  isPressed,
  play,
  ...delegated
}: Props) {
  return (
    <Wrapper
      {...delegated}
      onMouseDown={() => play({ id })}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          play({ id });
        }
      }}
    >
      <Trench>
        <Shaft />
      </Trench>
      <Surface data-is-pressed={String(isPressed)} aria-hidden="true">
        <BindingLabel>{keybinding}</BindingLabel>
      </Surface>

      <VisuallyHidden>
        Play a {id} sound. You can also press the {keybinding} key.
      </VisuallyHidden>
    </Wrapper>
  );
}

const Wrapper = styled.button`
  --size: 48px;
  --trench-color: hsl(202deg 20% 40%);
  --shaft-color: hsl(210deg 30% 75%);
  --shaft-shadow-color: hsl(210deg 20% 65%);
  --surface-color: hsl(210deg 30% 85%);
  --surface-bottom-edge-color: hsl(210deg 20% 90% / 1);
  --super-dark-shadow-color: hsl(210deg 20% 25%);
  --text-color: hsl(210deg 25% 10% / 0.8);
  --soft-glow-color: hsl(210deg 50% 93%);
  position: relative;
  display: block;
  border-radius: 6px;
  outline-offset: 3px;
  -webkit-tap-highlight-color: transparent;

  html[data-color-mode='dark'] & {
    --trench-color: black;
    --shaft-color: hsl(210deg 20% 25%);
    --shaft-shadow-color: hsl(210deg 20% 5% / 0.5);
    --surface-color: hsl(210deg 12% 25%);
    --surface-bottom-edge-color: hsl(210deg 15% 38% / 0.5);
    --super-dark-shadow-color: hsl(0deg 0% 0%);
    --text-color: hsl(210deg 25% 95% / 1);
    --soft-glow-color: hsl(210deg 60% 80% / 0.2);
  }
`;

const Trench = styled.span`
  position: relative;
  display: block;
  padding-inline: 2px;
  padding-bottom: 0;
  background: var(--trench-color);
  border-radius: 6px;
  overflow: hidden;
  overflow: clip;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 6px;
    box-shadow: inset 0px -1px 3px var(--super-dark-shadow-color);
    opacity: 0.75;
  }
`;

const Shaft = styled.span`
  display: block;
  position: relative;
  width: var(--size);
  height: var(--size);
  background: linear-gradient(
      to right,
      var(--shaft-shadow-color) 0%,
      hsl(0deg 0% 0% / 0) 20% 80%,
      var(--shaft-shadow-color) 100%
    ),
    var(--shaft-color);
  border-radius: 4px 4px 0px 0px;
`;

const BindingLabel = styled.span`
  position: relative;
  display: block;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  text-align: center;
  text-transform: uppercase;
  color: var(--text-color);
`;

const Surface = styled.span`
  position: absolute;
  inset: 0;
  margin-inline: auto;
  width: var(--size);
  height: var(--size);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border-bottom: 1px solid var(--surface-bottom-edge-color);
  background: var(--surface-color);
  transform: translateY(-4px);

  @media (prefers-reduced-motion: no-preference) {
    transition: transform 200ms;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at 50% 50%,
      var(--soft-glow-color) 0%,
      transparent 80%,
      transparent 100%
    );
  }

  ${Wrapper}:active &, &[data-is-pressed='true'] {
    transform: translateY(-1px);
    transition: transform 0ms;
  }
`;

export default React.memo(DrumButton);
